import { Fragment } from 'react'
import { Link, useLocation } from 'react-router'
import { SidebarTrigger, useSidebar } from 'tailwind-v4/components/sidebar'
import { resolveHref, resolveLabel } from '@/components/nav-v4/config'
import { VenueSwitcher } from '@/components/nav-v4/venue-switcher'
import { findOldNavChain } from '../old-nav-config'
import { useNavCtx } from './use-nav-ctx'
import { OldSchemePicker } from './old-scheme-picker'
import { OldSquareEntityPicker } from './old-square-entity-picker'

/**
 * Old-UI top bar: venue switcher on the left, breadcrumb (built from the old nav
 * tree) in the middle — the org switcher lives in the sidebar. On mobile the
 * breadcrumb collapses to just the current page label.
 */
export function OldTopBar() {
  const { pathname } = useLocation()
  const ctx = useNavCtx()
  const chain = findOldNavChain(pathname, ctx)
  // True loyalty-v2 scheme paths only: `/:orgId/loyalty/...` or
  // `/:orgId/venues/:serial/loyalty/...`. Checking "does 'loyalty' appear
  // anywhere in the path" would also match the unrelated, entity-scoped
  // Square Loyalty settings page at `/:orgId/square/loyalty` (see
  // docs/square-loyalty in the neo repo for why that name collision matters).
  const loyaltySegs = pathname.split('/').filter(Boolean)
  const inLoyalty =
    loyaltySegs[1] === 'loyalty' ||
    (loyaltySegs[1] === 'venues' && loyaltySegs[3] === 'loyalty')
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'

  // Discounts, Square Loyalty and Customer Groups are entity-scoped, not
  // venue-scoped: hide the venue picker entirely and show a Square entity
  // picker in its place. See docs/square-discounts and docs/square-loyalty
  // (neo repo).
  const orgId = pathname.split('/').filter(Boolean)[0] ?? ''
  const onDiscounts =
    Boolean(orgId) &&
    (pathname === `/${orgId}/square/discounts` ||
      pathname === `/${orgId}/square/loyalty` ||
      pathname === `/${orgId}/square/customer-groups`)

  // Full Bhuna at ORG scope has its own multi-select venue filter in the
  // report toolbar, so the global single-venue switcher stands down rather
  // than sitting next to it disagreeing. Mirrors the same rule in
  // new-top-bar.tsx: the two bars build independently from the same nav tree,
  // so a rule added to only one of them fixes the page for half the users.
  const onOrgFullBhuna =
    Boolean(orgId) &&
    pathname.startsWith(`/${orgId}/reports/full-bhuna`) &&
    !pathname.includes('/venues/')

  // Token background (not bg-white) so the bar blends with the sidebar and
  // page canvas — and a future palette swap only touches the token file.
  return (
    <header className="new-ui-theme old-ui-nav grid h-12 flex-none grid-cols-[1fr_minmax(0,auto)_1fr] items-center gap-3 border-b border-border bg-[var(--background)] px-4">
      <div className="flex min-w-0 items-center gap-2">
        {/* Mobile: the wordmark anchors the top-left (desktop controls
            below are all md-only, which left this cell empty on phones). */}
        <span className="logo-shimmer inline-block h-6 shrink-0 md:hidden">
          <img src="/favicon.svg" alt="Stampede" className="h-6 w-auto" />
        </span>
        {isCollapsed ? (
          // Re-open the collapsed sidebar — wrapped in `new-ui-theme` so the
          // v4 trigger's tokens resolve in the old UI. Desktop only (mobile
          // uses the floating Menu pill).
          <span className="new-ui-theme old-ui-nav hidden md:inline-flex">
            <SidebarTrigger className="hover:bg-black/5 dark:hover:bg-white/10" />
          </span>
        ) : null}
        {/* Desktop only — on mobile the venue switcher lives in the slide-out
            nav menu, so it's redundant in the top bar. */}
        {onDiscounts || onOrgFullBhuna ? null : (
          <span className="hidden md:inline-flex">
            <VenueSwitcher />
          </span>
        )}
        {/* Desktop only — on mobile the Square entity picker lives in the
            slide-out nav menu (like the venue switcher). */}
        {onDiscounts ? (
          <span className="hidden md:inline-flex">
            <OldSquareEntityPicker orgId={orgId} />
          </span>
        ) : null}
        {/* Desktop only — on mobile the scheme picker sits with the org and
            venue pills in the slide-out nav, same as the two above. */}
        {inLoyalty ? (
          <span className="hidden md:inline-flex">
            <OldSchemePicker />
          </span>
        ) : null}
      </div>
      <nav
        aria-label="Breadcrumb"
        className="flex min-w-0 items-center justify-center gap-1.5 text-sm"
      >
        {chain.length === 0 ? (
          <span className="font-semibold text-neutral-900 dark:text-white">
            Venues
          </span>
        ) : (
          chain.map((item, i) => {
            const isLast = i === chain.length - 1
            const href = resolveHref(item, ctx)
            const label = resolveLabel(item, ctx)
            return (
              <Fragment key={item.id}>
                {i > 0 ? (
                  <span className="text-black/25 dark:text-white/25">/</span>
                ) : null}
                {isLast || !href ? (
                  <span className="truncate font-semibold text-neutral-900 dark:text-white">
                    {label}
                  </span>
                ) : (
                  <Link
                    to={href}
                    className="shrink-0 truncate text-black/55 hover:text-black dark:text-white/55 dark:hover:text-white"
                  >
                    {label}
                  </Link>
                )}
              </Fragment>
            )
          })
        )}
      </nav>
      {/* An empty spacer cell, purely to keep the breadcrumb centred against
          the left controls. */}
      <div aria-hidden className="flex items-center justify-end" />
    </header>
  )
}
